// Numeric values mirror RAK.Shared.ContentSections (the API serialises enums as numbers).

export const SECTION_TYPE = {
  ADVANCED: 0, BRAND: 1, CATEGORY: 2, GENDER: 3, FRAGRANCE_NOTE: 4,
  CUSTOM: 5, COLLECTION: 6, PRICE: 7, PROMOTION: 8, FREE_SHIPPING: 9,
};

export const FILTER = {
  BRAND: 1, CATEGORY: 2, GENDER: 3, FRAGRANCE_NOTE: 4, PRODUCT_TYPE: 5, SEASON: 6, OCCASION: 7,
  CLASSIFICATION: 8, PRODUCT_KIND: 9, MIN_PRICE: 10, MAX_PRICE: 11, IS_NEW: 12,
  HAS_ACTIVE_PROMOTION: 13, HAS_FREE_SHIPPING: 14, PROMOTION: 15,
};

export const ZONE = { BEFORE_HERO: 1, AFTER_HERO: 2, BEFORE_FOOTER: 3 };

export const SECTION_TYPES = [
  { value: SECTION_TYPE.BRAND, en: 'Brand', ar: 'ماركة', descEn: 'Products of one or more brands', descAr: 'منتجات ماركة أو أكثر', preset: [{ type: FILTER.BRAND, value: '' }] },
  { value: SECTION_TYPE.CATEGORY, en: 'Category', ar: 'تصنيف', descEn: 'Products of a category', descAr: 'منتجات تصنيف معين', preset: [{ type: FILTER.CATEGORY, value: '' }] },
  { value: SECTION_TYPE.GENDER, en: 'Gender', ar: 'الجنس', descEn: 'Men, women or unisex fragrances', descAr: 'عطور رجالية أو نسائية أو للجنسين', preset: [{ type: FILTER.GENDER, value: 'male' }] },
  { value: SECTION_TYPE.FRAGRANCE_NOTE, en: 'Fragrance note', ar: 'نوتة عطرية', descEn: 'Perfumes containing a note (e.g. Iris)', descAr: 'عطور تحتوي نوتة معينة (مثل الآيريس)', preset: [{ type: FILTER.FRAGRANCE_NOTE, value: '' }] },
  { value: SECTION_TYPE.COLLECTION, en: 'Collection', ar: 'مجموعة', descEn: 'Product type, season or occasion', descAr: 'نوع المنتج أو الموسم أو المناسبة', preset: [{ type: FILTER.PRODUCT_TYPE, value: '' }] },
  { value: SECTION_TYPE.PRICE, en: 'Price range', ar: 'نطاق السعر', descEn: 'Products between two prices', descAr: 'منتجات ضمن نطاق سعر', preset: [{ type: FILTER.MIN_PRICE, value: '' }, { type: FILTER.MAX_PRICE, value: '' }] },
  { value: SECTION_TYPE.PROMOTION, en: 'Promotion', ar: 'عرض ترويجي', descEn: 'Products with a live discount (prices come from Discounts)', descAr: 'منتجات عليها خصم فعّال (الأسعار من الخصومات)', preset: [{ type: FILTER.HAS_ACTIVE_PROMOTION, value: 'true' }] },
  { value: SECTION_TYPE.FREE_SHIPPING, en: 'Free shipping', ar: 'شحن مجاني', descEn: 'Products that really ship free at checkout', descAr: 'منتجات تشحن مجاناً فعلياً عند الدفع', preset: [{ type: FILTER.HAS_FREE_SHIPPING, value: 'true' }] },
  { value: SECTION_TYPE.CUSTOM, en: 'Custom (manual)', ar: 'مخصص (يدوي)', descEn: 'Only the products you pick, in your order', descAr: 'المنتجات التي تختارها فقط وبالترتيب الذي تحدده', preset: [] },
  { value: SECTION_TYPE.ADVANCED, en: 'Advanced', ar: 'متقدم', descEn: 'Combine any conditions', descAr: 'اجمع أي شروط معاً', preset: [] },
];

export const sectionTypeMeta = (value) => SECTION_TYPES.find((t) => t.value === value) || SECTION_TYPES[SECTION_TYPES.length - 1];

// kind = how the value is edited; options = key into the /options payload
export const FILTER_TYPES = [
  { value: FILTER.BRAND, en: 'Brand', ar: 'الماركة', kind: 'lookup', options: 'brands' },
  { value: FILTER.CATEGORY, en: 'Category', ar: 'التصنيف', kind: 'lookup', options: 'categories' },
  { value: FILTER.GENDER, en: 'Gender', ar: 'الجنس', kind: 'enum', choices: [['male', 'Men', 'رجالي'], ['female', 'Women', 'نسائي'], ['unisex', 'Unisex', 'للجنسين']] },
  { value: FILTER.FRAGRANCE_NOTE, en: 'Fragrance note', ar: 'النوتة العطرية', kind: 'lookup', options: 'fragranceNotes' },
  { value: FILTER.PRODUCT_TYPE, en: 'Product type', ar: 'نوع المنتج', kind: 'lookup', options: 'productTypes' },
  { value: FILTER.SEASON, en: 'Season', ar: 'الموسم', kind: 'lookup', options: 'seasons' },
  { value: FILTER.OCCASION, en: 'Occasion', ar: 'المناسبة', kind: 'lookup', options: 'occasions' },
  { value: FILTER.CLASSIFICATION, en: 'Classification', ar: 'التصنيف التجاري', kind: 'enum', choices: [['Designer', 'Designer', 'تجاري'], ['Niche', 'Niche', 'متخصص']] },
  { value: FILTER.PRODUCT_KIND, en: 'Product kind', ar: 'شكل المنتج (طقم..)', kind: 'enum', choices: [['Standard', 'Standard', 'عادي'], ['Set', 'Set', 'طقم'], ['GiftSet', 'Gift set', 'طقم هدية'], ['Tester', 'Tester', 'تستر']] },
  { value: FILTER.MIN_PRICE, en: 'Minimum price (USD)', ar: 'أدنى سعر (دولار)', kind: 'number' },
  { value: FILTER.MAX_PRICE, en: 'Maximum price (USD)', ar: 'أعلى سعر (دولار)', kind: 'number' },
  { value: FILTER.IS_NEW, en: 'Marked as new', ar: 'منتج جديد', kind: 'bool' },
  { value: FILTER.HAS_ACTIVE_PROMOTION, en: 'Has an active promotion', ar: 'عليه عرض فعّال', kind: 'bool' },
  { value: FILTER.HAS_FREE_SHIPPING, en: 'Ships free', ar: 'شحن مجاني', kind: 'bool' },
  { value: FILTER.PROMOTION, en: 'Specific promotion', ar: 'عرض محدد', kind: 'lookup', options: 'discounts' },
];

export const filterMeta = (value) => FILTER_TYPES.find((f) => f.value === value);

export const LAYOUTS = [
  { value: 0, en: 'Standard product grid', ar: 'شبكة منتجات قياسية' },
  { value: 1, en: 'Horizontal carousel', ar: 'شريط أفقي متحرك' },
  { value: 2, en: 'Featured (first product large)', ar: 'مميز (المنتج الأول كبير)' },
  { value: 3, en: 'Banner with product grid', ar: 'بانر مع شبكة منتجات' },
];

// "Most viewed" is intentionally absent: no product view statistics are recorded, and none are invented.
export const AUTO_SORTS = [
  { value: 1, en: 'Newest products', ar: 'الأحدث' },
  { value: 2, en: 'Best-selling (real orders)', ar: 'الأكثر مبيعاً (طلبات فعلية)' },
  { value: 3, en: 'Price: low to high', ar: 'السعر: من الأقل' },
  { value: 4, en: 'Price: high to low', ar: 'السعر: من الأعلى' },
  { value: 5, en: 'Randomized (stable per day)', ar: 'عشوائي (ثابت خلال اليوم)' },
  { value: 0, en: 'Store default order', ar: 'ترتيب المتجر الافتراضي' },
];

export const ZONES = [
  { value: ZONE.BEFORE_HERO, en: 'Top of homepage (before the hero slider)', ar: 'أعلى الصفحة (قبل شريط الإعلانات)', shortEn: 'Before hero', shortAr: 'قبل الإعلانات' },
  { value: ZONE.AFTER_HERO, en: 'Main content (after the hero slider)', ar: 'المحتوى الرئيسي (بعد شريط الإعلانات)', shortEn: 'Main content', shortAr: 'المحتوى الرئيسي' },
  { value: ZONE.BEFORE_FOOTER, en: 'Before the footer', ar: 'قبل التذييل', shortEn: 'Before footer', shortAr: 'قبل التذييل' },
];

export const zoneMeta = (value) => ZONES.find((z) => z.value === value) || ZONES[1];

export const LIMIT_PRESETS = [6, 8, 10, 12, 20];

export const STATUSES = [
  { value: 'live', en: 'Live', ar: 'ظاهر', badge: 'badge-success' },
  { value: 'scheduled', en: 'Scheduled', ar: 'مجدول', badge: 'badge-info' },
  { value: 'expired', en: 'Expired', ar: 'منتهي', badge: 'badge-warning' },
  { value: 'inactive', en: 'Inactive', ar: 'معطّل', badge: 'badge-neutral' },
  { value: 'hidden', en: 'Hidden on website', ar: 'مخفي في الموقع', badge: 'badge-neutral' },
];

export const statusMeta = (value) => STATUSES.find((s) => s.value === value) || STATUSES[3];

/** ISO (UTC) → value for <input type="datetime-local"> in the admin's own timezone. */
export const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** datetime-local value (admin's timezone) → ISO UTC string, or null when empty. */
export const toUtcIso = (local) => {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** Pulls field → messages out of an ASP.NET ValidationProblem (or a plain error) response. */
export const extractErrors = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.errors && typeof data.errors === 'object') {
    const fields = {};
    Object.entries(data.errors).forEach(([k, v]) => {
      fields[k.charAt(0).toLowerCase() + k.slice(1)] = Array.isArray(v) ? v : [String(v)];
    });
    return { fields, messages: Object.values(fields).flat() };
  }
  const msg = data?.message || data?.title || err?.message || fallback;
  return { fields: {}, messages: [msg] };
};
